import React, { useEffect } from 'react';
import { NavLink, Outlet, useLocation, Navigate } from 'react-router-dom';
import { LayoutDashboard, GanttChart, Users, Wallet, UserCog, LogOut, Inbox, CalendarClock } from 'lucide-react';
import { useAuth } from './AuthContext';
import './crm.css';

function NavItem({ to, icon: Icon, children, end }) {
  return (
    <NavLink to={to} end={end} className={({ isActive }) => `crm-nav-link ${isActive ? 'active' : ''}`}>
      <Icon size={16} /> {children}
    </NavLink>
  );
}

export default function CrmLayout() {
  const { user, logout } = useAuth();
  const location = useLocation();

  useEffect(() => {
    document.body.classList.add('crm-body');
    return () => document.body.classList.remove('crm-body');
  }, []);

  // Auth gate
  if (user === null) {
    return <div style={{ padding: 60, textAlign: 'center', color: '#64748b' }}>Loading…</div>;
  }
  if (user === false) {
    return <Navigate to="/crm/login" state={{ from: location.pathname }} replace />;
  }

  const initial = (user.name || user.email || '?').charAt(0).toUpperCase();

  return (
    <div className="crm-shell">
      <aside className="crm-sidebar">
        <div className="brand">
          <img src="/logo.png" alt="Core Estimation" style={{ width: 38, height: 38, borderRadius: 8, background: '#fff', padding: 4, objectFit: 'contain' }} />
          <div>
            <div>Core Estimation</div>
            <div className="sub">CRM</div>
          </div>
        </div>

        <nav className="crm-nav">
          <NavItem to="/crm/dashboard" icon={LayoutDashboard} end>Dashboard</NavItem>
          <NavItem to="/crm/pipeline" icon={GanttChart}>Pipeline</NavItem>
          <NavItem to="/crm/leads" icon={Inbox}>Leads &amp; Deals</NavItem>
          <NavItem to="/crm/followups" icon={CalendarClock}>Follow-ups</NavItem>
          {user.role === 'admin' && (
            <>
              <div className="group-label">Admin</div>
              <NavItem to="/crm/accounts" icon={Wallet}>Accounts</NavItem>
              <NavItem to="/crm/team" icon={UserCog}>Team &amp; Roles</NavItem>
            </>
          )}
        </nav>

        <button
          onClick={logout}
          className="crm-nav-link"
          style={{
            marginTop: 24, background: 'rgba(255,255,255,.10)', border: 0,
            color: 'rgba(255,255,255,.85)', width: '100%', textAlign: 'left',
            padding: '10px 12px', borderRadius: 10, fontSize: 13.5, fontWeight: 600,
            cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10,
          }}
          data-testid="crm-logout"
        >
          <LogOut size={16} /> Sign out
        </button>
      </aside>

      <main className="crm-main">
        <header className="crm-topbar">
          <div>
            <h1>{titleFor(location.pathname)}</h1>
            <div className="sub">Welcome back, {user.name || user.email}</div>
          </div>
          <div className="crm-userchip">
            <span className="avatar">{initial}</span>
            <span>{user.role}</span>
          </div>
        </header>
        <div className="crm-content">
          <Outlet />
        </div>
      </main>

      <style>{`
        .crm-nav-link { display:flex;gap:10px;align-items:center;padding:10px 12px;border-radius:10px;
          color:rgba(255,255,255,.78); font-size:13.5px; font-weight:600; text-decoration:none;
          transition:background .15s,color .15s; }
        .crm-nav-link:hover { background:rgba(255,255,255,.10); color:#fff; }
        .crm-nav-link.active { background:rgba(255,255,255,.18); color:#fff; }
      `}</style>
    </div>
  );
}

function titleFor(path) {
  if (path.includes('/dashboard')) return 'Dashboard';
  if (path.includes('/pipeline')) return 'Sales Pipeline';
  if (path.includes('/leads')) return 'Leads & Deals';
  if (path.includes('/followups')) return 'Follow-ups';
  if (path.includes('/accounts')) return 'Accounts & Margin';
  if (path.includes('/team')) return 'Team & Roles';
  if (path.match(/\/crm\/deals\//)) return 'Deal Details';
  return 'Core Estimation CRM';
}
